import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  faSearch, faPlus, faEdit, faTrash, faTimes, faCheck,
  faQuestionCircle, faUsers, faUser, faArrowLeft, faSpinner,
  faExclamationTriangle, faEye, faEyeSlash, faCog
} from '@fortawesome/free-solid-svg-icons';
import { UsersService, User, CreateUserRequest, UpdateUserRequest, UserRole } from '../../services/users.service';
import { TenantService } from '../../services/tenant.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { DateFormatterService } from '../../services/date-formatter.service';
import { ItemListComponent, ListItem, ListConfig } from '../../components/item-list/item-list.component';
import { HeaderActionsComponent } from '../../components/header-actions/header-actions.component';
import { ButtonComponent } from '../../components/button/button.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { ModalUserConfigurationsComponent } from '../../components/modal-user-configurations/modal-user-configurations.component';
import { normalizeForSearch } from '../../utils/text-search.util';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    FontAwesomeModule,
    TranslateModule,
    ItemListComponent,
    HeaderActionsComponent,
    ButtonComponent,
    NgSelectModule,
    ModalUserConfigurationsComponent
],
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit, OnDestroy {
  users: User[] = [];
  filteredUsers: User[] = [];
  itemsForList: ListItem[] = [];
  searchTerm = '';
  loading = false;

  showCreateModal = false;
  showEditModal = false;
  showDeleteModal = false;
  showConfigurationsModal = false;

  selectedUser: User | null = null;
  nameError = '';
  emailError = '';
  isSubmitting = false;
  showPassword = false;

  userForm: CreateUserRequest = {
    username: '',
    email: '',
    role: 'EXTERNAL_USER',
    password: ''
  };

  availableRoles: { value: string; labelKey: string }[] = [];

  private usersSubscription?: Subscription;

  listConfig: ListConfig = {
    itemIcon: faUser,
    itemIconClass: 'user-icon',
    showMetadata: true,
    showType: true,
    typeLabels: {
      EXTERNAL_USER: 'external_user',
      INTERNAL_COLABORATOR: 'internal_colaborator',
      INTERNAL_DEVELOPER: 'internal_developer',
      ADMIN: 'admin'
    },
    emptyMessage: 'MESSAGE.NO.USERS',
    emptyMessageFiltered: 'MESSAGE.NO.USERS.SEARCH',
    actions: [
      { id: 'edit', label: 'BUTTON.EDIT', icon: faEdit, cssClass: 'edit-item' },
      { id: 'configurations', label: 'BUTTON.CONFIGURATIONS', icon: faCog, cssClass: 'config-item' },
      { id: 'delete', label: 'BUTTON.DELETE', icon: faTrash, cssClass: 'delete-item' }
    ]
  };

  faSearch = faSearch;
  faPlus = faPlus;
  faEdit = faEdit;
  faTrash = faTrash;
  faTimes = faTimes;
  faCheck = faCheck;
  faQuestionCircle = faQuestionCircle;
  faUsers = faUsers;
  faUser = faUser;
  faArrowLeft = faArrowLeft;
  faSpinner = faSpinner;
  faExclamationTriangle = faExclamationTriangle;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faCog = faCog;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private usersService: UsersService,
    private alertService: AlertService,
    private dateFormatter: DateFormatterService,
    private tenantService: TenantService
  ) {}

  ngOnInit(): void {
    this.availableRoles = this.usersService.getAvailableRoles();
    this.loadUsers();
  }

  onHeaderSearch(filters: { searchTerm: string }) {
    this.searchTerm = filters.searchTerm;
    this.onSearch();
  }

  ngOnDestroy(): void {
    this.usersSubscription?.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(_: Event): void {}

  get isSimpleEdit(): boolean {
    const provider = (this.selectedUser?.provider || '').toLowerCase();
    return provider === 'dilbert' || provider === 'extraidados';
  }

  loadUsers(): void {
    this.loading = true;
    this.usersService.list().subscribe({
      next: (response) => {
        this.users = response.users || [];
        this.applyFilters();
        this.loading = false;
      },
      error: (err) => {
        this.alertService.showError(err);
        this.loading = false;
        this.users = [];
        this.filteredUsers = [];
        this.itemsForList = [];
      }
    });
  }

  onSearch(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    const term = normalizeForSearch(this.searchTerm);
    if (!term) {
      this.filteredUsers = [...this.users];
    } else {
      this.filteredUsers = this.users.filter(u =>
        normalizeForSearch(u.username).includes(term) ||
        normalizeForSearch(u.email).includes(term) ||
        normalizeForSearch(u.role).includes(term)
      );
    }
    this.buildItemsForList();
  }

  private buildItemsForList(): void {
    this.itemsForList = this.filteredUsers.map(u => {
      const provider = (u.provider || 'local').toLowerCase();
      let tag = null;

      if (provider === 'local') {
        tag = {
          id: 'local',
          name: this.translate.instant('USERS.PROVIDERS.LOCAL'),
          bg_color: '#e3f2fd',
          text_color: '#1976d2'
        };
      } else if (provider === 'dilbert') {
        tag = {
          id: 'dilbert',
          name: this.translate.instant('USERS.PROVIDERS.DILBERT'),
          bg_color: '#fff3e0',
          text_color: '#f57c00'
        };
      } else if (provider === 'extraidados') {
        tag = {
          id: 'extraidados',
          name: this.translate.instant('USERS.PROVIDERS.EXTRAIDADOS'),
          bg_color: '#e8f5e9',
          text_color: '#388e3c'
        };
      }

      return {
        id: u.id,
        name: u.username,
        created_date: u.created_at ? this.dateFormatter.formatDate(u.created_at) : '',
        created_time: u.created_at ? this.dateFormatter.formatTime(u.created_at) : '',
        type: u.role,
        email: u.email,
        tag: tag
      };
    });
  }

  onItemClick(item: ListItem): void {
    const user = this.users.find(u => u.id === item.id);
    if (user) this.openEditModal(user);
  }

  onActionClick(event: { action: string; item: ListItem; event: Event }): void {
    const user = this.users.find(u => u.id === event.item.id);
    if (!user) return;
    if (event.action === 'edit') this.openEditModal(user);
    if (event.action === 'configurations') this.openConfigurationsModal(user);
    if (event.action === 'delete') this.openDeleteModal(user);
  }

  openCreateModal(): void {
    this.userForm = {
      username: '',
      email: '',
      role: 'EXTERNAL_USER',
      password: ''
    };
    this.clearErrors();
    this.showCreateModal = true;
  }

  openEditModal(user: User): void {
    this.selectedUser = user;
    this.userForm = {
      username: user.username,
      email: user.email,
      role: user.role,
      password: ''
    };
    this.clearErrors();
    this.showEditModal = true;
  }

  openDeleteModal(user: User): void {
    this.selectedUser = user;
    this.showDeleteModal = true;
  }

  openConfigurationsModal(user: User): void {
    this.selectedUser = user;
    this.showConfigurationsModal = true;
  }

  closeConfigurationsModal(): void {
    this.showConfigurationsModal = false;
    this.selectedUser = null;
  }

  closeModals(): void {
    this.showCreateModal = false;
    this.showEditModal = false;
    this.showDeleteModal = false;
    this.selectedUser = null;
    this.clearErrors();
    this.isSubmitting = false;
    this.showPassword = false;
  }

  clearErrors(): void {
    this.nameError = '';
    this.emailError = '';
  }

  createUser(): void {
    if (this.isSubmitting) return;
    this.clearErrors();
    const errors = this.usersService.validateUserData(this.userForm);
    if (errors.length) {
      this.handleValidationErrors(errors);
      return;
    }
    this.isSubmitting = true;
    this.usersService.create(this.userForm).subscribe({
      next: () => {
        this.closeModals();
        this.loadUsers();
        this.isSubmitting = false;
        this.alertService.showSuccess('Usuário criado com sucesso');
      },
      error: (err) => {
        this.handleServerErrors(err);
        this.isSubmitting = false;
      }
    });
  }

  updateUser(): void {
    if (!this.selectedUser || this.isSubmitting) return;
    this.clearErrors();

    if (this.isSimpleEdit) {
      const role = this.userForm.role as UserRole;
      this.isSubmitting = true;
      this.usersService.updateRole(this.selectedUser.id, role).subscribe({
        next: () => {
          this.closeModals();
          this.loadUsers();
          this.isSubmitting = false;
          this.alertService.showSuccess('Usuário atualizado com sucesso');
        },
        error: (err) => {
          this.handleServerErrors(err);
          this.isSubmitting = false;
        }
      });
      return;
    }

    const data: UpdateUserRequest = {
      username: this.userForm.username,
      email: this.userForm.email,
      role: this.userForm.role,
      password: this.userForm.password
    };

    const errors = this.usersService.validateUserData(data, this.selectedUser.id);
    if (errors.length) {
      this.handleValidationErrors(errors);
      return;
    }

    this.isSubmitting = true;
    this.usersService.update(this.selectedUser.id, data).subscribe({
      next: () => {
        this.closeModals();
        this.loadUsers();
        this.isSubmitting = false;
        this.alertService.showSuccess('Usuário atualizado com sucesso');
      },
      error: (err) => {
        this.handleServerErrors(err);
        this.isSubmitting = false;
      }
    });
  }

  deleteUser(): void {
    if (!this.selectedUser) return;
    this.usersService.delete(this.selectedUser.id).subscribe({
      next: () => {
        this.closeModals();
        this.loadUsers();
        this.alertService.showSuccess('Usuário excluído com sucesso');
      },
      error: (err) => this.alertService.showError(err)
    });
  }

  private handleValidationErrors(errors: string[]): void {
    errors.forEach(e => {
      if (e.toLowerCase().includes('user') || e.toLowerCase().includes('nome')) this.nameError = e;
      if (e.toLowerCase().includes('email')) this.emailError = e;
    });
    this.alertService.showError(errors.join('\n'));
  }

  private handleServerErrors(err: any): void {
    if (err?.status === 409 || err?.error?.message?.includes('existe')) {
      const msg = err.error?.message || '';
      if (msg.includes('username') || msg.includes('usuário')) this.nameError = 'Já existe um usuário com este nome';
      if (msg.includes('email')) this.emailError = 'Já existe um usuário com este e-mail';
    }
    this.alertService.showError(err);
  }

  onUsernameChange(): void {
    if (this.nameError) this.nameError = '';
  }

  onEmailChange(): void {
    if (this.emailError) this.emailError = '';
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  getRoleLabel(role: string): string {
    const found = this.availableRoles.find(r => r.value === role);
    return found ? this.translate.instant(found.labelKey) : role;
  }

  goBack(): void {
    this.router.navigate(this.tenantService.buildRoute('/settings'));
  }

  get hasActiveFilters(): boolean {
    return !!this.searchTerm.trim();
  }

  trackById(_: number, item: ListItem): string | number {
    return item.id;
  }
}